import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useId, useMemo} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import SearchField from "@khanacademy/wonder-blocks-search-field";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useFastlyQuery} from "@khan/data-gql";
import {CONTENT_ITEM_QUERY, useContentSearch} from "@khan/guide-core";

import {ClickableContentCell} from "./clickable-content-cell.tsx";
import {RequestedInputLabel} from "./requested-input-label.tsx";
import {RequestedInputTextArea} from "./requested-input-text-area.tsx";
import {RequestedInputTextBox} from "./requested-input-text-box.tsx";

import searchIcon from "@phosphor-icons/core/regular/magnifying-glass.svg";
import sparkleIcon from "@phosphor-icons/core/regular/sparkle.svg";

// NOTE(reid): This is a subset of `LearnableContentKind`. For now, at least,
// we only support these three content kinds in this content search input.
type SearchableContentKind = "Article" | "Exercise" | "Video";

interface Props {
    label: string;
    description?: string;
    variableName: string;
    values: Record<string, string>;
    disabled?: boolean;
    required?: boolean;
    setValues: (value: React.SetStateAction<Record<string, string>>) => void;
    error?: string | null;
}

export const RequestedInputContentSearch = ({
    label,
    description,
    variableName,
    disabled,
    required,
    values,
    setValues,
    error,
}: Props): React.ReactElement => {
    const id = useId();

    const {query, setQuery, loading, searchResults} = useContentSearch({
        allowedTopicAncestors: ["math", "science", "humanities"],
        contentKinds: ["Article", "Exercise", "Video"],
        numResults: 3,
    });

    const selectedContentDescriptor = values[variableName];

    const contentItemResult = useFastlyQuery(CONTENT_ITEM_QUERY, {
        variables: {
            descriptors: [selectedContentDescriptor],
        },
        skip: !selectedContentDescriptor,
    });

    const contentItemDetails = useMemo(() => {
        const data =
            contentItemResult.status === "success"
                ? contentItemResult.data.contentForDescriptors?.[0]
                : null;
        if (!data) {
            return null;
        }

        return {
            id: data.id,
            title: data.translatedTitle ?? "",
            description: data.translatedDescription ?? "",
            contentDescriptor: data.contentDescriptor ?? "",
            contentKind: data.contentKind as SearchableContentKind,
            courseTitle: data.lesson?.unit?.course?.translatedTitle ?? "",
            standards: data.mappedStandards?.filter(isTruthy),
        };
    }, [contentItemResult]);

    if (values.userProvidedTopic || selectedContentDescriptor === "custom") {
        return (
            <View style={styles.userProvidedInputs}>
                <RequestedInputTextBox
                    label={label}
                    description={description}
                    value={values.userProvidedTopic || ""}
                    onChange={(newTopic) =>
                        setValues({
                            ...values,
                            userProvidedTopic: newTopic,
                        })
                    }
                    disabled={disabled}
                    required={true}
                    error={null}
                />
                <RequestedInputTextArea
                    label={t`Standards or learning objective`}
                    description={t`Provide a full standard description or learning objective associated with your topic.`}
                    value={values.userProvidedStandards || ""}
                    onChange={(newStandards) =>
                        setValues({
                            ...values,
                            userProvidedStandards: newStandards,
                        })
                    }
                    disabled={disabled}
                    required={true}
                    error={null}
                />
                <RequestedInputTextArea
                    label={t`Notes`}
                    description={t`Add any details about the topic or teaching tools you'd like to use`}
                    value={values.userProvidedNotes || ""}
                    onChange={(newNotes) =>
                        setValues({
                            ...values,
                            userProvidedNotes: newNotes,
                        })
                    }
                    disabled={disabled}
                    required={true}
                    error={null}
                />
                <Button
                    disabled={disabled}
                    startIcon={searchIcon}
                    kind="tertiary"
                    style={styles.backToContentButton}
                    onClick={() => {
                        setQuery("");
                        setValues((prev) => ({
                            ...prev,
                            [variableName]: "",
                            userProvidedTopic: query,
                            userProvidedStandards: "",
                            userProvidedNotes: "",
                        }));
                    }}
                >
                    {t`Use Khan Academy content instead`}
                </Button>
            </View>
        );
    }

    if (selectedContentDescriptor) {
        return (
            <RequestedInputLabel
                id={id}
                label={label}
                description={description}
                required={required}
            >
                <ClickableContentCell
                    chosen
                    title={contentItemDetails?.title ?? ""}
                    description={contentItemDetails?.description ?? ""}
                    contentDescriptor={
                        contentItemDetails?.contentDescriptor ?? ""
                    }
                    courseTitle={contentItemDetails?.courseTitle ?? ""}
                    mappedStandards={contentItemDetails?.standards}
                    disabled={disabled}
                    onClick={() => {
                        setQuery("");
                        setValues((prev) => ({
                            ...prev,
                            [variableName]: "",
                            userProvidedTopic: "",
                            userProvidedStandards: "",
                            userProvidedNotes: "",
                        }));
                    }}
                />
            </RequestedInputLabel>
        );
    }

    return (
        <RequestedInputLabel
            // NOTE(reid): This is needed because the WB SearchField appends a
            // "-field" to the `id` it puts on the text input - so we update the
            // `id` for the label's htmlFor to match.
            id={id + "-field"}
            label={label}
            description={description}
            required={required}
            error={error}
        >
            <View style={styles.container}>
                <SearchField
                    id={id}
                    value={query}
                    onChange={setQuery}
                    placeholder={t`Search Khan Academy...`}
                    // eslint-disable-next-line @khanacademy/ts-no-error-suppressions -- see below
                    // @ts-expect-error this prop works but isn't recognized
                    autoComplete="off"
                />
                {query.length > 0 && (
                    <View style={styles.results}>
                        {loading && (
                            <View style={styles.loading}>
                                <CircularSpinner size="small" />
                                <LabelMedium>{t`Loading...`}</LabelMedium>
                            </View>
                        )}
                        {!loading &&
                            searchResults.map((result, i) => {
                                const learnableContent =
                                    result?.learnableContent;
                                if (!learnableContent) {
                                    return <></>;
                                }

                                const title =
                                    learnableContent.translatedTitle || "";
                                const description =
                                    learnableContent.translatedDescription ||
                                    "";
                                const placeInList =
                                    i === 0 ? "first" : "middle";
                                const contentDescriptor =
                                    learnableContent.contentDescriptor || "";
                                const course = [
                                    learnableContent.parentTopic,
                                    learnableContent.parentTopic?.parent,
                                    learnableContent.parentTopic?.parent
                                        ?.parent,
                                ].find(
                                    (ancestor) =>
                                        ancestor?.contentKind === "Course",
                                );
                                const courseTitle =
                                    course?.translatedTitle || "";

                                const standards =
                                    learnableContent.mappedStandards?.filter(
                                        isTruthy,
                                    );

                                return (
                                    <ClickableContentCell
                                        key={result.contentId}
                                        title={title}
                                        description={description}
                                        mappedStandards={standards}
                                        contentDescriptor={contentDescriptor}
                                        courseTitle={courseTitle}
                                        placeInList={placeInList}
                                        onClick={() => {
                                            setValues((prev) => ({
                                                ...prev,
                                                [variableName]:
                                                    contentDescriptor,
                                                userProvidedTopic: "",
                                                userProvidedStandards: "",
                                                userProvidedNotes: "",
                                            }));
                                            setQuery("");
                                        }}
                                    />
                                );
                            })}
                        <ClickableContentCell
                            title={t`Provide your own content...`}
                            description={t`Khanmigo will need a few details to help you write`}
                            courseTitle=""
                            contentDescriptor=""
                            placeInList="last"
                            onClick={() => {
                                setValues((prev) => ({
                                    ...prev,
                                    [variableName]: "custom",
                                    userProvidedTopic: query,
                                    userProvidedStandards: "",
                                    userProvidedNotes: "",
                                }));
                                setQuery("");
                            }}
                            icon={sparkleIcon}
                        />
                    </View>
                )}
            </View>
        </RequestedInputLabel>
    );
};

const styles = StyleSheet.create({
    container: {
        position: "relative",
    },
    results: {
        marginBlockStart: `calc(-1 * ${sizing.size_010})`,
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        width: "100%",
        boxShadow: `0px 2px 2px 0 ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: border.radius.radius_040,
    },
    loading: {
        flexDirection: "row",
        padding: sizing.size_160,
        color: semanticColor.core.foreground.neutral.default,
        borderBlockEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        gap: sizing.size_160,
    },
    userProvidedInputs: {
        gap: sizing.size_240,
    },
    backToContentButton: {
        width: "fit-content",
    },
});
